import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import * as SolidIcons from '@heroicons/react/24/solid';

// --- Reusable Form Components ---
const FormInput = ({ id, name, label, type, value, onChange, required = true }) => (
    <div>
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
        <input type={type} id={id} name={name} value={value} onChange={onChange} required={required} className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white" />
    </div>
);

const SelectInput = ({ id, name, label, value, onChange, options, required = true }) => (
    <div>
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">{label}</label>
        <select id={id} name={name} value={value} onChange={onChange} required={required} className="w-full p-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-700 dark:text-white">
            <option value="">Select an icon...</option>
            {options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
        </select>
    </div>
);

const Checkbox = ({ id, name, label, checked, onChange }) => (
    <div className="flex items-center">
        <input type="checkbox" id={id} name={name} checked={checked} onChange={onChange} className="h-4 w-4 text-orange-600 border-gray-300 rounded focus:ring-orange-500" />
        <label htmlFor={id} className="ml-2 block text-sm text-gray-900 dark:text-gray-300">{label}</label>
    </div>
);

// Get a list of all solid Heroicon names for the dropdown
const heroIconNames = Object.keys(SolidIcons).filter(key => key.endsWith('Icon'));

export default function ManageStatsPage() {
    const { token } = useAuth();
    const [stats, setStats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [editingStat, setEditingStat] = useState(null);

    const [formData, setFormData] = useState({ title: '', stat_value: 0, icon_name: '', is_visible: true, display_order: 0 });

    const fetchData = useCallback(async () => {
        if (!token) return;
        setLoading(true);
        try {
            // ✅ CORRECTED URL
            const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/stats`, { headers: { 'Authorization': `Bearer ${token}` }});
            if (!response.ok) throw new Error('Could not fetch stats.');
            const data = await response.json();
            setStats(data);
        } catch (err) { 
            setError(err.message); 
        } finally { 
            setLoading(false); 
        }
    }, [token]);

    useEffect(() => { fetchData(); }, [fetchData]);

    useEffect(() => {
        if (editingStat) {
            setFormData(editingStat);
        } else {
            setFormData({ title: '', stat_value: 0, icon_name: '', is_visible: true, display_order: 0 });
        }
    }, [editingStat]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setMessage('');
        setError('');

        // ✅ CORRECTED URL LOGIC
        const url = editingStat 
            ? `${process.env.REACT_APP_API_URL}/admin/stats/${editingStat.id}`
            : `${process.env.REACT_APP_API_URL}/admin/stats`;

        const method = editingStat ? 'PUT' : 'POST';

        try {
            const response = await fetch(url, {
                method: method,
                headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
                body: JSON.stringify(formData)
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message);

            setMessage(editingStat ? 'Stat updated successfully!' : 'Stat created successfully!');
            setEditingStat(null);
            fetchData();
        } catch (err) {
            setError(err.message);
        } finally {
            setIsSubmitting(false);
        }
    };
    
    const handleDelete = async (statId) => {
        if (!window.confirm('Are you sure you want to delete this stat?')) return;
        try {
            // ✅ CORRECTED URL
            const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/stats/${statId}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message);
            setMessage(data.message);
            fetchData();
        } catch (err) {
            setError(err.message);
        }
    };

    if (loading) return <p className="p-8 text-center">Loading Site Stats...</p>;

    return (
        <div className="space-y-8">
            <h1 className="text-4xl font-extrabold text-gray-900 dark:text-white">Manage Site Statistics</h1>
            {error && <p className="p-4 text-center font-semibold text-red-500 bg-red-100 dark:bg-red-900/50 rounded-lg">{error}</p>}
            {message && <p className="p-4 text-center font-semibold text-green-500 bg-green-100 dark:bg-green-900/50 rounded-lg">{message}</p>}
            
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 items-start">
                <div className="lg:col-span-1">
                    <div className="bg-white dark:bg-gray-800 p-8 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700">
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <h2 className="text-2xl font-bold text-gray-800 dark:text-white">
                                {editingStat ? 'Edit Stat' : 'Add New Stat'}
                            </h2>
                            <FormInput id="title" name="title" label="Title" type="text" value={formData.title} onChange={handleChange} />
                            <FormInput id="stat_value" name="stat_value" label="Value (Number)" type="number" value={formData.stat_value} onChange={handleChange} />
                            <SelectInput id="icon_name" name="icon_name" label="Icon" value={formData.icon_name} onChange={handleChange} options={heroIconNames} />
                            <FormInput id="display_order" name="display_order" label="Display Order" type="number" value={formData.display_order} onChange={handleChange} />
                            <Checkbox id="is_visible" name="is_visible" label="Visible on Homepage" checked={formData.is_visible} onChange={handleChange} />

                            <div className="flex gap-4">
                                {editingStat && <button type="button" onClick={() => setEditingStat(null)} className="w-full py-3 bg-gray-500 text-white font-bold rounded-lg hover:bg-gray-600">Cancel Edit</button>}
                                <button type="submit" disabled={isSubmitting} className="w-full py-3 bg-orange-500 text-white font-bold rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50">
                                    {isSubmitting ? 'Saving...' : (editingStat ? 'Save Changes' : 'Create Stat')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>

                <div className="lg:col-span-2">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border border-gray-200 dark:border-gray-700">
                        <h2 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">Current Stats</h2>
                        <div className="space-y-2">
                            {stats.map(stat => (
                                <div key={stat.id} className="flex justify-between items-center p-3 border-b dark:border-gray-600 last:border-b-0">
                                    <div className="flex items-center gap-4">
                                        <div className="text-orange-500">
                                            {SolidIcons[stat.icon_name] ? React.createElement(SolidIcons[stat.icon_name], { className: 'w-8 h-8' }) : <SolidIcons.QuestionMarkCircleIcon className="w-8 h-8" />}
                                        </div>
                                        <div>
                                            <p className="font-bold text-gray-800 dark:text-white">{stat.title}</p>
                                            <p className="text-sm text-gray-500">Value: {stat.stat_value} | Order: {stat.display_order} | Visible: {stat.is_visible ? 'Yes' : 'No'}</p>
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <button onClick={() => setEditingStat(stat)} className="p-2 text-blue-500 hover:bg-blue-100 dark:hover:bg-blue-900/50 rounded-full" title="Edit">✏️</button>
                                        <button onClick={() => handleDelete(stat.id)} className="p-2 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/50 rounded-full" title="Delete">🗑️</button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}